/** Document-storage mutations used by entity surfaces. */

import { scheduledActionKeys } from '@app/features/routines/queries/keys';
import {
  copyItem,
  deleteItem,
  moveToFolder,
} from '@core/component/FileList/itemOperations';
import { toast } from '@core/component/Toast/Toast';
import {
  enableGraphqlSoup,
  isFeatureEnabled,
} from '@core/constant/featureFlags';
import { throwOnErr } from '@core/util/result';
import { deleteAgentSession } from '@queries/agent-session/entity-mutations';
import { callKeys } from '@queries/call/keys';
import { queryClient } from '@queries/client';
import {
  getSoupEntityById,
  invalidateSoupEntity,
  invalidateSoupQueriesReferencing,
  optimisticUpdateSoupEntity,
  removeSearchEntities,
  removeSoupEntities,
  removeSoupEntitiesFromQueriesReferencing,
} from '@queries/soup/cache';
import { refreshActiveGraphqlSoupQueries } from '@queries/soup/graphql/active-queries';
import {
  createGraphqlSoupDeletion,
  GRAPHQL_SOUP_DELETE_MUTATION_KEY,
  GRAPHQL_SOUP_DELETE_RETENTION_MS,
} from '@queries/soup/graphql/optimistic-deletions';
import { soupKeys } from '@queries/soup/keys';
import { ownTouchStamp } from '@queries/soup/normalized-cache/own-touch';
import { callServiceClient } from '@service-call/client';
import { scheduledActionClient } from '@service-scheduled-action/client';
import { useMutation } from '@tanstack/solid-query';
import { batch } from 'solid-js';
import { type EntityData, getEntityProjectId } from '../types/entity';
import { BulkDeleteFailure } from './bulk-delete-result';

function invalidateDeletedDssItems(entities: EntityData[]): void {
  if (entities.some((e) => e.type === 'call')) {
    queryClient.invalidateQueries({ queryKey: callKeys._def });
  }
  if (entities.some((e) => e.type === 'routine')) {
    const deletedIds = new Set(
      entities.filter((e) => e.type === 'routine').map((e) => e.id)
    );
    queryClient.setQueryData(
      scheduledActionKeys.list.queryKey,
      (current: unknown) => {
        if (!Array.isArray(current)) return current;
        return current.filter(
          (item: { id?: string }) => !item.id || !deletedIds.has(item.id)
        );
      }
    );
    queryClient.invalidateQueries({
      queryKey: scheduledActionKeys.list.queryKey,
    });
    for (const scheduleId of deletedIds) {
      queryClient.removeQueries({
        queryKey: scheduledActionKeys.detail({ scheduleId }).queryKey,
      });
      queryClient.removeQueries({
        queryKey: scheduledActionKeys.history({ scheduleId }).queryKey,
      });
    }
  }
}

async function settleGraphqlDeletes(
  entities: EntityData[],
  deletions: Promise<boolean>[]
): Promise<boolean[]> {
  const outcomes = await Promise.allSettled(deletions);
  const results = outcomes.map(
    (outcome) => outcome.status === 'fulfilled' && outcome.value
  );
  // Process confirmed successes before surfacing sibling failures. Never remove
  // failed schedules or skip call invalidation because another API threw.
  try {
    invalidateDeletedDssItems(entities.filter((_, index) => results[index]));
  } catch (error) {
    console.error('Failed to reconcile deleted item caches', error);
  }
  const failure = outcomes.find((outcome) => outcome.status === 'rejected');
  if (results.some((success) => !success)) {
    if (failure && !results.some(Boolean)) throw failure.reason;
    throw new BulkDeleteFailure(entities, results, failure?.reason);
  }
  return results;
}

export function createBulkDeleteDssItemsMutation() {
  const graphqlSoupEnabled = isFeatureEnabled(enableGraphqlSoup);
  const isDeletable = (entity: EntityData) => {
    const type = entity.type;
    return (
      type === 'agent_session' ||
      type === 'chat' ||
      type === 'document' ||
      type === 'project' ||
      type === 'call' ||
      type === 'routine'
    );
  };
  const isCurrentContext = (context: unknown) =>
    queryClient
      .getMutationCache()
      .getAll()
      .some((mutation) => mutation.state.context === context);
  return useMutation(() => ({
    ...(graphqlSoupEnabled
      ? {
          mutationKey: GRAPHQL_SOUP_DELETE_MUTATION_KEY,
          gcTime: GRAPHQL_SOUP_DELETE_RETENTION_MS,
        }
      : {}),
    mutationFn: async (entities: EntityData[]) => {
      const deletable = entities.filter(isDeletable);
      const deletions = deletable.map(async (e) => {
        if (e.type === 'agent_session') {
          await deleteAgentSession(e.id);
          return true;
        }
        if (e.type === 'call') {
          return throwOnErr(() =>
            callServiceClient.deleteCallRecord(e.id)
          ).then(() => true);
        }
        if (e.type === 'routine') {
          return throwOnErr(() =>
            scheduledActionClient.deleteSchedule({ scheduleId: e.id })
          ).then(() => true);
        }
        return deleteItem({ id: e.id, itemType: e.type });
      });
      const results = graphqlSoupEnabled
        ? await settleGraphqlDeletes(deletable, deletions)
        : await Promise.all(deletions);
      if (!graphqlSoupEnabled) invalidateDeletedDssItems(deletable);
      return results;
    },
    onMutate: async (entities: EntityData[]) => {
      const deletable = entities.filter(isDeletable);
      const ids = new Set(deletable.map((e) => e.id));
      const soupSnapshot = removeSoupEntities(ids);
      const searchSnapshot = removeSearchEntities(ids);
      return {
        soupSnapshot,
        searchSnapshot,
        ...(graphqlSoupEnabled
          ? { graphqlDeletion: createGraphqlSoupDeletion([...ids]) }
          : {}),
      };
    },
    onSettled: graphqlSoupEnabled
      ? (results, error, entities, context) => {
          if (!context?.graphqlDeletion) return;
          // Logout/cache clearing can outlive an in-flight API call. Do not
          // repopulate session-local tombstones after their mutation was cleared.
          if (!isCurrentContext(context)) {
            context.graphqlDeletion.release();
            return;
          }
          const outcomes =
            error instanceof BulkDeleteFailure ? error.results : results;
          const deletedIds = entities
            .filter(isDeletable)
            .flatMap((entity, index) =>
              outcomes?.[index] === true ? [entity.id] : []
            );
          // API completion closes the mutation; display state survives it and
          // independently retries revalidation. Failed ids are restored now.
          context?.graphqlDeletion?.settle(deletedIds);
        }
      : undefined,
    onError: (error, entities, context) => {
      if (graphqlSoupEnabled && context && !isCurrentContext(context)) {
        context.graphqlDeletion?.release();
        return;
      }
      const partialFailure =
        graphqlSoupEnabled &&
        error instanceof BulkDeleteFailure &&
        error.deletedEntities.length > 0;
      if (partialFailure) {
        const deletedIds = new Set(
          error.deletedEntities.map((entity) => entity.id)
        );
        // The legacy transactions hold whole snapshots. Restore failed rows,
        // then reapply confirmed removals in one reactive batch so successes
        // never reappear in REST fallback lists or search results.
        batch(() => {
          context?.soupSnapshot.rollback();
          context?.searchSnapshot.rollback();
          removeSoupEntities(deletedIds);
          removeSearchEntities(deletedIds);
        });
      } else {
        context?.soupSnapshot.rollback();
        context?.searchSnapshot.rollback();
      }
      console.error(`Failed to delete dss items`, entities, error);
      toast.failure(partialFailure ? error.message : 'Failed to delete items');
    },
  }));
}

function invalidateAfterMove(
  entityIds: string[],
  destinationProjectId: string,
  hasProjects: boolean,
  failed?: boolean
) {
  if (failed) {
    toast.failure('Failed to move item');
  }

  // Covers queries already containing the entities (including the source folder's list)
  for (const id of entityIds) {
    invalidateSoupEntity(id);
  }
  // Destination folder's list queries don't contain the entities yet, so match by project id in the key
  invalidateSoupQueriesReferencing([destinationProjectId]);
  queryClient.invalidateQueries({ queryKey: ['entity'] });
  // If moving a project, invalidate all project queries since nested projects' breadcrumbs change too
  if (hasProjects) {
    queryClient.invalidateQueries({
      queryKey: ['project'],
    });
  }
  if (isFeatureEnabled(enableGraphqlSoup)) {
    return refreshActiveGraphqlSoupQueries();
  }
}

export function createMoveToProjectDssEntityMutation() {
  return useMutation(() => ({
    mutationFn: async ({
      entity: { id, type },
      project: { id: projectId },
    }: {
      entity: EntityData & { type: 'document' | 'chat' | 'project' };
      project: { id: string };
    }) => {
      const success = await moveToFolder({
        itemType: type,
        id,
        folderId: projectId,
      });

      return { success };
    },
    onMutate: async ({
      entity: { id, type },
      project: { id: projectId },
    }: {
      entity: EntityData & { type: 'document' | 'chat' | 'project' };
      project: { id: string };
    }) => {
      if (type !== 'project') {
        const current = getSoupEntityById(id);
        return optimisticUpdateSoupEntity({
          tag: type,
          data: { id, projectId },
          frecency_score: current?.frecency_score ?? 0,
        });
      }
    },
    onSettled: (data, error, { entity: { id, type }, project }, context) => {
      const failed = data?.success === false || !!error;
      if (failed) {
        context?.rollback();
        console.error(`Failed to move dss item ${id}`, data, error);
      }

      return invalidateAfterMove([id], project.id, type === 'project', failed);
    },
  }));
}

export function createBulkRemoveFromProjectDssEntityMutation() {
  const isUnsupportedEntity = (entity: EntityData) => {
    const type = entity.type;
    return (
      type !== 'chat' &&
      type !== 'document' &&
      type !== 'project' &&
      type !== 'email'
    );
  };

  return useMutation(() => ({
    mutationFn: async ({ entities }: { entities: EntityData[] }) => {
      if (entities.some(isUnsupportedEntity)) {
        throw new Error(`Unsupported entity type provided`);
      }

      const results = await Promise.all(
        entities.map((entity) =>
          moveToFolder({
            itemType: entity.type as 'document' | 'chat' | 'project' | 'email',
            id: entity.id,
            folderId: null,
          })
        )
      );

      if (results.some((r) => !r)) {
        throw new Error(`One or more DSS items failed to move`);
      }

      return { success: true };
    },

    onMutate: async ({ entities }: { entities: EntityData[] }) => {
      const moveableEntities = entities.filter(
        (e): e is typeof e & { type: 'document' | 'chat' | 'email' } =>
          e.type === 'document' || e.type === 'chat' || e.type === 'email'
      );
      const txns = moveableEntities.map((e) => {
        const current = getSoupEntityById(e.id);
        const tag = e.type === 'email' ? 'emailThread' : e.type;
        return optimisticUpdateSoupEntity({
          tag,
          data: { id: e.id, projectId: null },
          frecency_score: current?.frecency_score ?? 0,
          // A move is an Edited activity, i.e. a touch (own-touch.ts).
          touched_at: ownTouchStamp(e.id),
        });
      });

      // Drop the rows from the source folders' views right away; other views
      // keep the entities
      const sourceProjectIds = [
        ...new Set(
          entities.flatMap((e) => {
            const projectId =
              getEntityProjectId(e) || getSoupEntityProjectId(e.id);
            return projectId ? [projectId] : [];
          })
        ),
      ];
      txns.push(
        removeSoupEntitiesFromQueriesReferencing(
          new Set(entities.map((e) => e.id)),
          sourceProjectIds
        )
      );

      return { txns, sourceProjectIds };
    },

    onSettled: (data, error, { entities }, context) => {
      const failed = data?.success === false || !!error;
      if (failed) {
        // Reverse order: each txn's snapshot captures the previous txns' writes
        context?.txns.toReversed().forEach((txn) => txn.rollback());
        console.error(
          `Failed to remove dss items from folder`,
          entities,
          error
        );
        toast.failure('Failed to remove items from folder');
      }

      // Queries still containing the entities
      for (const entity of entities) {
        invalidateSoupEntity(entity.id);
      }
      // The source folders' views no longer contain the entities after the
      // optimistic removal, so match them by project id in the key
      invalidateSoupQueriesReferencing(context?.sourceProjectIds ?? []);
      queryClient.invalidateQueries({ queryKey: ['entity'] });
      // Removed projects' breadcrumbs (and nested projects') change too
      if (entities.some((e) => e.type === 'project')) {
        queryClient.invalidateQueries({ queryKey: ['project'] });
      }
      if (isFeatureEnabled(enableGraphqlSoup)) {
        return refreshActiveGraphqlSoupQueries();
      }
    },
  }));
}

/** Source project of a soup-cached entity ('project' items nest it as parentId). */
function getSoupEntityProjectId(entityId: string): string | undefined {
  const cached = getSoupEntityById(entityId);
  if (!cached) return undefined;
  if (cached.tag === 'project') return cached.data.parentId ?? undefined;
  if ('projectId' in cached.data) return cached.data.projectId ?? undefined;
  return undefined;
}

export function createBulkCopyDssEntityMutation() {
  // Only support chat + document, same as single-copy version
  const isUnsupportedEntity = (entity: EntityData) => {
    const type = entity.type;
    return type !== 'chat' && type !== 'document';
  };

  return useMutation(() => ({
    mutationFn: async ({
      entities,
      name,
    }: {
      entities: (EntityData & { name: string })[];
      name: string | ((oldName: string) => string);
    }) => {
      if (entities.some(isUnsupportedEntity)) {
        throw new Error(`Unsupported entity type provided`);
      }

      const results = await Promise.all(
        entities.map((e) =>
          copyItem({
            itemType: e.type as 'document' | 'chat',
            id: e.id,
            name: typeof name === 'function' ? name(e.name) : name,
          })
        )
      );

      if (results.some((r) => !r)) {
        throw new Error(`One or more DSS items failed to copy`);
      }

      return { success: true };
    },

    onMutate: async () => {
      // For copy, no optimistic update — new IDs unknown until server
      await queryClient.cancelQueries({
        queryKey: soupKeys.items._def,
      });
      await queryClient.cancelQueries({
        queryKey: soupKeys.astItems._def,
      });
    },

    onSettled: (data, error, { entities }) => {
      if (error) {
        console.error(`Failed bulk copy`, entities, data, error);
        toast.failure('Failed to copy items');
      }

      // Trigger refetch so new items appear
      queryClient.invalidateQueries({
        queryKey: soupKeys.items._def,
      });
      queryClient.invalidateQueries({
        queryKey: soupKeys.astItems._def,
      });
      queryClient.invalidateQueries({ queryKey: ['entity'] });
      if (isFeatureEnabled(enableGraphqlSoup)) {
        return refreshActiveGraphqlSoupQueries();
      }
    },
  }));
}

export function createBulkMoveToProjectDssEntityMutation() {
  const isUnsupportedEntity = (entity: EntityData) => {
    const type = entity.type;
    return (
      type !== 'chat' &&
      type !== 'document' &&
      type !== 'project' &&
      type !== 'email'
    );
  };

  return useMutation(() => ({
    mutationFn: async ({
      entities,
      project,
    }: {
      entities: (EntityData & { name: string })[];
      project: { id: string; name: string };
    }) => {
      if (entities.some(isUnsupportedEntity)) {
        throw new Error(`Unsupported entity type provided`);
      }

      const results = await Promise.all(
        entities.map((entity) =>
          moveToFolder({
            itemType: entity.type as 'document' | 'chat' | 'project' | 'email',
            id: entity.id,
            folderId: project.id,
          })
        )
      );

      if (results.some((r) => !r)) {
        throw new Error(`One or more DSS items failed to move`);
      }

      return { success: true };
    },

    onMutate: async ({
      entities,
      project,
    }: {
      entities: (EntityData & { name: string })[];
      project: { id: string; name: string };
    }) => {
      const moveableEntities = entities.filter(
        (e): e is typeof e & { type: 'document' | 'chat' | 'email' } =>
          e.type === 'document' || e.type === 'chat' || e.type === 'email'
      );
      return moveableEntities.map((e) => {
        const current = getSoupEntityById(e.id);
        const tag = e.type === 'email' ? 'emailThread' : e.type;
        return optimisticUpdateSoupEntity({
          tag,
          data: { id: e.id, projectId: project.id },
          frecency_score: current?.frecency_score ?? 0,
          // A move is an Edited activity, i.e. a touch (own-touch.ts).
          touched_at: ownTouchStamp(e.id),
        });
      });
    },

    onSettled: (data, error, { entities, project }, context) => {
      const failed = data?.success === false || !!error;
      if (failed) {
        context?.forEach((txn) => txn.rollback());
        console.error(`Failed to bulk move dss items`, entities, data, error);
      }

      return invalidateAfterMove(
        entities.map((e) => e.id),
        project.id,
        entities.some((e) => e.type === 'project'),
        failed
      );
    },
  }));
}
